// Take a room away from a user — the mobile half of the Rooms panel in the
// desktop's UserAccessScreen, reached by long-pressing the room.
//
// It is `DeleteFromLocationRoomsMapping` (core/admin/userAccess unallotRoom) and
// NOT a delete: the Rooms row, its devices and everybody else's access are all
// untouched. There is no endpoint that deletes a room, which is why the wording
// here never says "delete" — an operator who reads it as one will expect the
// room to be gone for the other users too, and it will not be.
//
// ⚠️ IT LEAVES THE DEVICE MAPPINGS BEHIND. Dropping the room mapping does not
// touch a single UserDeviceMapping row, so the user keeps rows pointing into a
// room they can no longer open. That is the "half-assignment" that makes a
// device impossible to account for later, and it is invisible from the phone
// once the room is gone — so the count is fetched and shown BEFORE the write,
// exactly as the location-level confirmation does. Clearing them stays a
// deliberate act on the desktop's User Access screen; this dialog reports, it
// does not tidy up on its own.

import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography,
  Alert, CircularProgress, Box, useTheme,
} from '@mui/material';
import { useApp } from '../../../core/state/AppContext';
import { unallotRoom, danglingInRoom } from '../../../core/admin/userAccess';

export default function UnmapRoomDialog({
  open, room, locationId, locationName, onClose, onDone,
}) {
  const theme = useTheme();
  const c = theme.sli;
  const { userId, viewAsUser, allLocations } = useApp();

  const [counts, setCounts] = useState(null);   // null = still counting
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const roomId = room?.RoomID;

  useEffect(() => {
    if (!open || roomId == null) return;
    setCounts(null);
    setError('');
    danglingInRoom(userId, locationId, roomId)
      .then(setCounts)
      .catch(() => setCounts({ devices: 0 }));
  }, [open, roomId, userId, locationId]);

  const submit = async () => {
    setBusy(true);
    setError('');
    try {
      await unallotRoom(userId, locationId, roomId);
      onDone();
    } catch (e) {
      // ⚠️ Never judged by affectedRows — these delete endpoints return counts
      // belonging to other tables. The caller re-reads the list instead.
      setError('The room could not be unmapped. Nothing has been changed.');
    } finally {
      setBusy(false);
    }
  };

  const whose = viewAsUser
    ? `${[viewAsUser.Surname, viewAsUser.Name].filter(Boolean).join(' ')} (ID ${viewAsUser.UserId})`
    : `you (ID ${userId})`;

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      PaperProps={{ sx: { bgcolor: c.back, borderRadius: '20px' } }}
    >
      <DialogTitle sx={{ color: c.text, fontWeight: 700 }}>
        {`Remove “${room?.RoomName || 'this room'}”?`}
      </DialogTitle>

      <DialogContent>
        <Typography sx={{ color: c.text, fontSize: 14, mb: 2 }}>
          {`This takes the room away from ${whose} in ${locationName}. `
            + 'The room itself, its devices and anyone else’s access are not changed.'}
        </Typography>

        {/* ⚠️ All Locations ignores user mappings entirely, so the room on
            screen may never have been mapped to anybody — the delete would then
            match no row and appear to do nothing. Say so rather than let it
            look broken. */}
        {allLocations && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            All locations is on, so this list ignores user mappings. If the room
            was never mapped to this user there is nothing to remove.
          </Alert>
        )}

        {counts == null ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={18} sx={{ color: c.text }} />
            <Typography sx={{ color: c.text, fontSize: 13 }}>
              Checking what is inside…
            </Typography>
          </Box>
        ) : counts.devices > 0 && (
          <Alert severity="info">
            {`${counts.devices} device${counts.devices === 1 ? '' : 's'} in this room `
              + 'stay mapped and will not be reachable from the app afterwards. '
              + 'Clear them on the desktop’s User Access screen if that is not what you want.'}
          </Alert>
        )}

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={busy} sx={{ color: c.text }}>Cancel</Button>
        <Button
          variant="contained"
          color="error"
          onClick={submit}
          disabled={busy || counts == null}
        >
          {busy ? 'Removing…' : 'Remove room'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
